import React from "react";
import { motion } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import ThreeBackground from "./ThreeBackground";
import { FaLinkedin, FaWhatsapp, FaEnvelope, FaDownload } from "react-icons/fa";
import { Link } from "react-scroll";

const RESUME_URL =
  "https://drive.google.com/file/d/1votzGnOmgdfOcV-FRgTl68EQ8YamNWCx/view?usp=share_link";

const Hero = () => {
  return (
    <section className="relative h-screen flex overflow-hidden items-center justify-center scanline">
      {/* Base background */}
      <div className="absolute inset-0 bg-dark-950" />
      <div className="absolute inset-0 grid-overlay" />

      {/* Dark vignette in the CENTER only — left & right edges stay clear for 3D */}
      <div className="hero-dark-overlay absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_50%_50%,rgba(13,17,23,0.82)_0%,rgba(13,17,23,0.4)_55%,transparent_100%)]" />

      <ThreeBackground />

      {/* Centered text content */}
      <div className="relative z-10 text-center px-4 sm:px-6 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-5 sm:mb-6"
        >
          <span className="font-mono text-xs sm:text-sm text-gray-400">
            {"// "}welcome to my portfolio
          </span>
        </motion.div>

        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-5 leading-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <span className="text-white">Hi, I'm </span>
          <span className="text-glow">Petrus</span>
        </motion.h1>

        <motion.div
          className="mb-5 sm:mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <span className="font-mono text-sm sm:text-base md:text-lg">
            <span className="text-sky-400">const</span>{" "}
            <span className="text-white">role</span>{" "}
            <span className="text-sky-400">=</span>{" "}
            <TypeAnimation
              sequence={[
                '"Full-Stack Developer"', 2000,
                '"Back-End Developer"', 2000,
                '"Go Developer"', 2000,
                '"Finance × Tech"', 2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="text-emerald-400"
            />
            <span className="text-sky-400 animate-blink">_</span>
          </span>
        </motion.div>

        <motion.p
          className="text-xs sm:text-sm md:text-base text-gray-300 mb-8 sm:mb-10 max-w-xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          Full-Stack Developer with a strong foundation in Finance, bringing
          analytical thinking to scalable backend and web solutions.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <Link
            to="portfolio"
            smooth={true}
            offset={-70}
            duration={500}
            className="btn-tech-filled cursor-pointer w-full sm:w-auto justify-center"
          >
            {">"} view_projects
          </Link>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-tech w-full sm:w-auto justify-center"
          >
            <FaDownload className="text-xs" />
            download_cv
          </a>
        </motion.div>

        {/* Social */}
        <motion.div
          className="flex items-center justify-center gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          {[
            { icon: <FaLinkedin />, href: "https://www.linkedin.com/in/petrus-ardi-tama/" },
            { icon: <FaWhatsapp />, href: "https://wa.me/6281585021394" },
            { icon: <FaEnvelope />, href: "mailto:petrus.arditama@gmail.com" },
          ].map((s, i) => (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-sky-400 transition-all duration-300 text-xl hover:drop-shadow-[0_0_8px_rgba(56,189,248,0.5)]"
            >
              {s.icon}
            </a>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 hidden sm:flex"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      >
        <span className="font-mono text-[10px] text-gray-500">scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-sky-400/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
